/**
 * Tenant names and slugs, shared by the API (which enforces them) and the web
 * client (which suggests a slug while the platform manager types the name).
 *
 * The slug appears in links and can never
 * change after creation, so the rule is deliberately narrow: lower-case ASCII
 * letters, digits and hyphens, 3 to 40 characters. The database enforces the
 * same rule (ck_tenants_slug in db/migrations/0005_tenants.sql).
 */

export const TENANT_NAME_MIN = 2;
export const TENANT_NAME_MAX = 120;
export const TENANT_SLUG_MIN = 3;
export const TENANT_SLUG_MAX = 40;

// Lowercase letters, digits and single inner hyphens: no leading, trailing or
// doubled hyphen, so a slug reads cleanly in a link.
const SLUG_PATTERN = /^(?=.{3,40}$)[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function isValidSlug(value: unknown): value is string {
  return typeof value === 'string' && SLUG_PATTERN.test(value);
}

/** German spellings first: "Flugsportverein Bühl" should become "...-buehl", not "...-buhl". */
const TRANSLITERATION: Readonly<Record<string, string>> = {
  ä: 'ae',
  ö: 'oe',
  ü: 'ue',
  ß: 'ss',
};

/**
 * A slug suggestion from a name: "FSV 1910 Karlsruhe e.V." → "fsv-1910-karlsruhe-e-v".
 *
 * A suggestion, not a guarantee: the result can be shorter than three
 * characters (a name like "A") or already taken, so the caller still checks
 * `isValidSlug` and the API still answers 409 for a duplicate.
 */
export function slugify(name: string): string {
  const lowered = name.trim().toLowerCase();
  const transliterated = lowered.replace(/[äöüß]/g, (ch) => TRANSLITERATION[ch] ?? ch);
  const ascii = transliterated.normalize('NFKD').replace(/[̀-ͯ]/g, '');
  const hyphenated = ascii.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return hyphenated.slice(0, TENANT_SLUG_MAX).replace(/-+$/g, '');
}

/**
 * The tenant code: the prefix of every damage reference (`FSV-000042`).
 * Two to five upper-case ASCII letters, set at creation and never changed —
 * it is printed on paper, said on the phone and typed into searches. The
 * database enforces the same rule (ck_tenants_code) and uniqueness.
 */
export const TENANT_CODE_MIN = 2;
export const TENANT_CODE_MAX = 5;

const CODE_PATTERN = /^[A-Z]{2,5}$/;

export function isValidTenantCode(value: unknown): value is string {
  return typeof value === 'string' && CODE_PATTERN.test(value);
}

/** Letters only, umlauts spelled out, accents dropped, so "Bühl" gives "BUEHL". */
function asciiLetters(word: string): string {
  return word
    .replace(/[äöüÄÖÜß]/g, (ch) => {
      const spelled = TRANSLITERATION[ch.toLowerCase()] ?? ch;
      return ch === ch.toLowerCase() ? spelled : spelled.toUpperCase();
    })
    .normalize('NFKD')
    .replace(/[^A-Za-z]/g, '');
}

/**
 * A code suggestion from a name. The rule, in order:
 *
 *  1. A trailing legal form "e.V." is dropped.
 *  2. The name is split into words; digits and punctuation separate words and
 *     contribute nothing.
 *  3. A word written in capitals (at least two letters, e.g. FSV, LSV) counts
 *     with all its letters; every other word with its initial.
 *  4. The result is upper-cased and cut to five letters.
 *  5. Shorter than two letters? Then the first five letters of the first word.
 *
 *   "LSV Rheinstetten"             → LSVR
 *   "FSV 1910 Karlsruhe e.V."      → FSVK
 *   "Luftsportverein Rheinstetten" → LR
 *   "Segelfluggruppe"              → SEGEL
 *
 * Like `slugify`, a suggestion: it can be taken or too short (a name like
 * "A"), so the caller still checks `isValidTenantCode` and the API answers 409.
 */
export function suggestTenantCode(name: string): string {
  const withoutLegalForm = name.trim().replace(/\s*\be\.\s*v\.?$/i, '');
  const words = withoutLegalForm
    .split(/[^\p{L}]+/u)
    .map(asciiLetters)
    .filter((w) => w.length > 0);
  const initials = words
    .map((w) => (w.length >= 2 && w === w.toUpperCase() ? w : w[0]!))
    .join('')
    .toUpperCase()
    .slice(0, TENANT_CODE_MAX);
  if (initials.length >= TENANT_CODE_MIN) return initials;
  return (words[0] ?? '').toUpperCase().slice(0, TENANT_CODE_MAX);
}
